import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';
import { STORAGE_KEY as THEME_STORAGE_KEY, LOGOUT_EVENT } from './ThemeContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [previousPage, setPreviousPage] = useState('main');

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');
        const storedUserData = localStorage.getItem('user_data');
        
        if (token && refreshToken) {
          // Set as authenticated immediately for better UX
          setIsAuthenticated(true);
          setIsValidating(true);
          
          // Load user data from localStorage if available
          if (storedUserData) {
            try {
              const userData = JSON.parse(storedUserData);
              setUser(userData);
            } catch (parseError) {
              console.log('Failed to parse stored user data:', parseError.message);
            }
          }
          
          // Validate token in background
          try {
            const userData = await authService.getCurrentUser();
            setUser(userData);
            localStorage.setItem('user_data', JSON.stringify(userData));
            console.log('Token validation successful');
          } catch (error) {
            console.log('Token validation failed, attempting refresh:', error.message);
            // Token invalid, try refresh
            try {
              await authService.refreshToken();
              const userData = await authService.getCurrentUser();
              setUser(userData);
              localStorage.setItem('user_data', JSON.stringify(userData));
              console.log('Token refresh successful');
            } catch (refreshError) {
              console.log('Token refresh failed, clearing auth state:', refreshError.message);
              // Both failed, clear auth
              logout();
            }
          } finally {
            setIsValidating(false);
          }
        } else {
          // No tokens, clear any existing auth state
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.log('Auth check error:', error.message);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
      // Store user data in localStorage as fallback
      localStorage.setItem('user_data', JSON.stringify(userData));
      return response;
    } catch (error) {
      throw error;
    }
  };

  const googleLogin = async (accessToken) => {
    try {
      const response = await authService.googleLogin(accessToken);
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
      // Store user data in localStorage as fallback
      localStorage.setItem('user_data', JSON.stringify(userData));
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    // Clear user preferences that persist in localStorage so they are not visible when logged out
    try {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } catch (_) {}
    setUser(null);
    setIsAuthenticated(false);
    window.dispatchEvent(new CustomEvent(LOGOUT_EVENT));
  }, []);

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Fetch user data when needed (e.g., for profile pages)
  const fetchUserData = async () => {
    try {
      if (isAuthenticated) {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        // Store user data in localStorage
        localStorage.setItem('user_data', JSON.stringify(userData));
        return userData;
      }
      return null;
    } catch (error) {
      // If fetching user data fails, try to use stored data as fallback
      console.log('Failed to fetch user data:', error);
      const storedUserData = localStorage.getItem('user_data');
      if (storedUserData) {
        try {
          const userData = JSON.parse(storedUserData);
          setUser(userData);
          return userData;
        } catch (parseError) {
          console.log('Failed to parse stored user data:', parseError.message);
        }
      }
      // Only logout if we have no fallback data
      logout();
      throw error;
    }
  };

  // Check if user should be logged out based on token state
  const checkLogoutCondition = useCallback(() => {
    const token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    
    // If no tokens exist, user should be logged out
    if (!token || !refreshToken) {
      if (isAuthenticated) {
        console.log('No tokens found, logging out user');
        logout();
      }
    }
  }, [isAuthenticated, logout]);

  // Validate tokens periodically to handle session expiration
  const validateTokens = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (token && refreshToken && isAuthenticated) {
        // Try to fetch user data to verify tokens are still valid
        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          localStorage.setItem('user_data', JSON.stringify(userData));
          console.log('Periodic token validation successful');
        } catch (error) {
          // If validation fails, try to refresh token first
          console.log('Periodic token validation failed, attempting refresh:', error.message);
          try {
            await authService.refreshToken();
            // If refresh succeeds, try to get user data again
            const userData = await authService.getCurrentUser();
            setUser(userData);
            localStorage.setItem('user_data', JSON.stringify(userData));
            console.log('Periodic token refresh successful');
          } catch (refreshError) {
            // If refresh also fails, clear auth state
            console.log('Periodic token refresh failed, logging out');
            logout();
          }
        }
      }
    } catch (error) {
      console.log('Periodic validation error:', error.message);
      // If there's an unexpected error, clear auth state
      logout();
    }
  }, [isAuthenticated, logout]);

  // Set up periodic token validation and logout checks
  useEffect(() => {
    if (isAuthenticated) {
      // Check logout conditions every 30 seconds
      const logoutInterval = setInterval(checkLogoutCondition, 30000);
      
      // Validate tokens every 45 minutes (background session check)
      const validationInterval = setInterval(validateTokens, 45 * 60 * 1000);
      
      return () => {
        clearInterval(logoutInterval);
        clearInterval(validationInterval);
      };
    }
  }, [isAuthenticated, validateTokens, checkLogoutCondition]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isValidating,
    previousPage,
    setPreviousPage,
    login,
    googleLogin,
    logout,
    register,
    fetchUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
